const T = require('prop-types');
const { push: Push } = require('connected-react-router');
const { goBack: GoBack } = require('connected-react-router');
const { makeActionCreator } = require('../utils/redux-helpers');
const { CLASSES: Types } = require('../action-types');
const Domain = require('../domain');
const { ALL_GROUPS_SORT_TYPE } = require('../utils/constants');
const Capitalize = require('lodash/capitalize');


module.exports = (context) => {

    const api = context.api.nearpeer;
    const actions = context.actions;
    const selectors = context.selectors.all;
    const redux = context.redux.hooks;

    const refreshClass = async ({ dispatch, payload, state, force }) => {

        const { id } = payload.request;

        if (!force && !selectors.classCouldRefresh(state, id)) {
            return Promise.resolve();
        }

        const { payload: classPayload } = await dispatch(actions.dataFetching.fetchClass({ id }));

        const classId = classPayload.result.result.class;
        const channelSid = classPayload.result.entities.classes[classId].sid;

        if (!channelSid) {
            return;
        }

        return dispatch(actions.dataFetching.fetchChannel({ channelSid }));
    };

    return {
        join: makeActionCreator(Types.JOIN, { id: T.string, name: T.string, userId: T.string }, {
            async: async (groupInfo) => {

                const { id } = groupInfo;

                try {
                    await api.classes.join(groupInfo);
                }
                catch (err) {

                    const responsePayload = err?.response?.data || err?.data || err?.output?.payload || {};
                    const message = responsePayload?.options?.frontendDisplay && responsePayload?.message
                        ? responsePayload.message
                        : 'Unable to join this group right now.';

                    redux.dispatch(actions.alerts.notification.push({
                        message
                    }));

                    throw err;
                }

                const isAnnouncement = selectors.getGroupIsAnnouncement(redux.getState(), id);

                if (isAnnouncement) {
                    // TODO: fetchAnnouncementConversation({ id }) doesn't seem to work
                    // There are other areas we should use fetchAnnouncementConversation({ id })
                    // instead of the full fetchAnnouncementConversations(), but this bug needs to be fixed.
                    redux.dispatch(actions.dataFetching.fetchAnnouncementConversations());
                    redux.dispatch(actions.dataFetching.fetchAnnouncementMessages({ id }));
                }
            },
            after: ({ dispatch, payload, ...other }) => {

                const { id, name, userId } = payload.request;

                dispatch(actions.dataFetching.fetchSync());

                dispatch(actions.alerts.notification.push({
                    message: userId ? `Added user to "${name || 'class'}"` : `Joined "${name || 'class'}"`,
                    action: {
                        label: 'Undo',
                        onExecute: () => dispatch(actions.classes.leave(payload.request))
                    }
                }));

                dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

                const channelSid = selectors.getClassSid_byId(redux.getState(), id);

                // If for another user, don't refresh
                if (userId) {
                    return;
                }

                if (channelSid) {
                    dispatch(actions.dataFetching.fetchChannelLastMessage({ channelSid }));
                    refreshClass({ dispatch, payload, ...other });
                }
            }
        }),
        leave: makeActionCreator(Types.LEAVE, { id: T.string, name: T.string, userId: T.string }, {
            async: api.classes.leave,
            after: ({ dispatch, payload }) => {

                const { id, name, userId } = payload.request;

                dispatch(actions.dataFetching.fetchSync());

                dispatch(actions.alerts.notification.push({
                    message: userId ? `Removed user from "${name || 'group'}"` : `Left "${name || 'group'}"`,
                    action: {
                        label: 'Undo',
                        onExecute: () => dispatch(actions.classes.join(payload.request))
                    }
                }));

                dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

                const channelSid = selectors.getClassSid_byId(redux.getState(), id);

                if (channelSid) {
                    dispatch(actions.dataFetching.clearChannelLastMessage({ channelSid }));
                }
            }
        }),
        create: makeActionCreator(Types.CREATE, Domain.Class, {
            async: async (group) => {

                const {
                    facilitator,
                    shortDescription,
                    additionalDescription,
                    classLocation,
                    ...others
                } = group;

                try {
                    return await api.classes.create({
                        professor: facilitator,
                        department: shortDescription,
                        schedule: additionalDescription,
                        location: classLocation,
                        type: 'group',
                        ...others
                    });
                }
                catch (err) {

                    redux.dispatch(actions.alerts.notification.push({
                        message: `Failed to create group: ${err.message}`
                    }));
                }
            },
            after: ({ dispatch, payload }) => {

                const { result: classId } = payload.result;
                const { name } = payload.result.entities.classes[classId];

                dispatch(actions.alerts.notification.push({
                    message: `Created group ${name}`,
                    action: {
                        label: 'View',
                        onExecute: () => dispatch(Push(`/app/classes/${classId}`))
                    }
                }));

                dispatch(Push('/app/classes'));
            }
        }),
        createNoRedirect: makeActionCreator(Types.CREATE, Domain.Class, {
            async: async (class_) => {

                const {
                    facilitator,
                    shortDescription,
                    additionalDescription,
                    classLocation,
                    classRoles:roles,
                    ...others
                } = class_;

                try {
                    return await api.classes.create({
                        professor: facilitator,
                        department: shortDescription,
                        schedule: additionalDescription,
                        location: classLocation,
                        roles,
                        ...others
                    });
                }
                catch (err) {

                    redux.dispatch(actions.alerts.notification.push({
                        message: `Failed to create group: ${err.message}`
                    }));
                }
            },
            after: ({ dispatch, payload }) => {

                const { result: classId } = payload.result;
                const { name } = payload.result.entities.classes[classId];

                dispatch(actions.alerts.notification.push({
                    message: `Created group ${name}`,
                    action: {
                        label: 'View',
                        onExecute: () => dispatch(Push(`/app/classes/${classId}`))
                    }
                }));
            }
        }),
        update: makeActionCreator(Types.UPDATE, Domain.Class, {
            async: async (group) => {

                const {
                    facilitator,
                    shortDescription,
                    additionalDescription,
                    classLocation,
                    roles,
                    ...others
                } = group;

                delete others.isAnnouncement;

                try {
                    await api.classes.update({
                        professor: facilitator,
                        department: shortDescription,
                        schedule: additionalDescription,
                        location: classLocation,
                        roles,
                        ...others
                    });

                    return;
                }
                catch (err) {

                    redux.dispatch(actions.alerts.notification.push({
                        message: `Failed to update group: ${err.message}`
                    }));
                }
            },
            after: async ({ dispatch, payload }) => {

                const { request: { id: groupId, name } } = payload;

                await dispatch(actions.dataFetching.fetchClass({ id: groupId }));

                dispatch(GoBack());

                dispatch(actions.alerts.notification.push({
                    message: `Updated group "${name}"`,
                    action: {
                        label: 'View',
                        onExecute: () => dispatch(Push(`/app/classes/${groupId}`))
                    }
                }));
            }
        }),
        updateNotificationLevel: makeActionCreator(Types.UPDATE_NOTIFICATION_LEVEL, { id: T.string, notificationLevel: T.string }, {
            async: api.classes.updateNotificationLevel,
            after: ({ dispatch, payload, ...other }) => {

                const { notificationLevel } = payload.request;

                dispatch(actions.alerts.notification.push({
                    message: `Set notification level to "${Capitalize(notificationLevel)}"`
                }));

                dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

                refreshClass({ dispatch, payload, force: true, ...other });
            }
        }),
        setSearchText: makeActionCreator(Types.SET_SEARCH_TEXT, { id: T.string, text: T.string }),
        setAllGroupSortType: makeActionCreator(Types.SET_ALL_GROUPS_SORTING_TYPE, { sortType: T.oneOf([ALL_GROUPS_SORT_TYPE.BY_NAME,ALL_GROUPS_SORT_TYPE.BY_DATE_UPDATED]) }),
        clearSearchText: makeActionCreator(Types.CLEAR_SEARCH_TEXT, { id: T.string })
    };
};
